import {
  erc20Abi,
  maxUint256,
  parseUnits,
  type Address,
  type Hash,
  type WalletClient,
} from "viem";

import {
  LOOPER_AJNA_ABI,
  LOOPER_MORPHO_ABI,
} from "@/config/looper-abi";
import { AJNA_POSITION_ABI } from "@/config/ajna-position-abi";
import { AJNA_POOL_INFO_UTILS_ABI } from "@/config/ajna-pool-abi";
import { MORPHO_BLUE_ABI } from "@/config/morpho-abi";
import { VENUES, AJNA_POOL_INFO_UTILS, type VenueId } from "@/config/contracts";
import { CADC, USDC } from "@/config/tokens";
import { buildPublicClient } from "@/lib/wallet/clients";

// Ajna's absolute highest bucket index — effectively "no limit".
// Used as fallback when the pool is empty or poolPricesInfo is unavailable.
const AJNA_MAX_FENWICK_INDEX = 7388n;

// How many buckets of headroom to add above the current LUP index.
// Each bucket ≈ 0.5% price, so 10 ≈ 5% buffer.
const AJNA_LIMIT_INDEX_BUFFER = 10n;

const readAjnaLimitIndex = async (): Promise<bigint> => {
  const client = buildPublicClient();
  const [result] = await client.multicall({
    allowFailure: true,
    contracts: [{
      address: AJNA_POOL_INFO_UTILS,
      abi: AJNA_POOL_INFO_UTILS_ABI,
      functionName: "poolPricesInfo",
      args: [VENUES.ajna.contract],
    }],
  });
  if (result.status === "success") {
    return result.result[5] + AJNA_LIMIT_INDEX_BUFFER; // lupIndex + buffer
  }
  // Pool is empty or PoolInfoUtils unreachable — fall back to max index.
  // Oracle and minLeverageBps still guard the user.
  return AJNA_MAX_FENWICK_INDEX;
};

export type LoopRequest = {
  readonly account: Address;
  readonly principal: number;
  readonly targetLeverage: number;
  readonly slippageBps: number;
  readonly venue: VenueId;
  /** Ajna only: per-step borrow fraction in bps. Ignored for Morpho. */
  readonly ajnaStepFactorBps: number;
};

const leverageToBps = (leverage: number): number =>
  Math.round(leverage * 10_000);

/**
 * Morpho: multicall previewLoop + previewLoopIterative to pick the fastest path.
 * Ajna: always iterative — no flash loan.
 */
const selectLoopPath = async (
  venue: VenueId,
  principalRaw: bigint,
  leverageBps: number,
  stepFactorBps: number,
): Promise<"flash" | "iterative" | null> => {
  const client = buildPublicClient();
  const looper = VENUES[venue].looper;

  if (venue === "ajna") {
    const [flash, iterative] = await client.multicall({
      contracts: [
        {
          address: looper,
          abi: LOOPER_AJNA_ABI,
          functionName: "previewLoop",
          args: [principalRaw, leverageBps, stepFactorBps],
        },
        {
          address: looper,
          abi: LOOPER_AJNA_ABI,
          functionName: "previewLoopIterative",
          args: [principalRaw, leverageBps, stepFactorBps],
        },
      ],
    });
    if (flash.status === "success" && flash.result[0]) return "flash";
    if (iterative.status === "success" && iterative.result[0]) return "iterative";
    return null;
  }

  const [flash, iterative] = await client.multicall({
    contracts: [
      {
        address: looper,
        abi: LOOPER_MORPHO_ABI,
        functionName: "previewLoop",
        args: [principalRaw, leverageBps],
      },
      {
        address: looper,
        abi: LOOPER_MORPHO_ABI,
        functionName: "previewLoopIterative",
        args: [principalRaw, leverageBps],
      },
    ],
  });
  if (flash.status === "success" && flash.result[0]) return "flash";
  if (iterative.status === "success" && iterative.result[0]) return "iterative";
  return null;
};

/**
 * Set the authorised operator on the user's AjnaPosition to the given looper.
 * Called once on first use, and again after any looper redeployment.
 */
export const setAjnaPositionOperator = async (
  wallet: WalletClient,
  args: { readonly account: Address; readonly positionAddress: Address; readonly looper: Address },
): Promise<Hash> => {
  return await wallet.writeContract({
    account: args.account,
    chain: null,
    address: args.positionAddress,
    abi: AJNA_POSITION_ABI,
    functionName: "setOperator",
    args: [args.looper],
  });
};

/**
 * Grant the looper an (unbounded) CADC allowance. One-time per wallet.
 */
export const approveCadc = async (
  wallet: WalletClient,
  args: { readonly account: Address; readonly looper: Address },
): Promise<Hash> => {
  return await wallet.writeContract({
    account: args.account,
    chain: null,
    address: CADC.address,
    abi: erc20Abi,
    functionName: "approve",
    args: [args.looper, maxUint256],
  });
};

/**
 * Authorize the looper on Morpho Blue. Morpho venue only.
 */
export const authorizeMorpho = async (
  wallet: WalletClient,
  args: { readonly account: Address; readonly looper: Address },
): Promise<Hash> => {
  return await wallet.writeContract({
    account: args.account,
    chain: null,
    address: VENUES.morpho.contract,
    abi: MORPHO_BLUE_ABI,
    functionName: "setAuthorization",
    args: [args.looper, true],
  });
};

/**
 * Open a leveraged CADC position.
 * - Morpho: prefers flash loan, falls back to iterative.
 * - Ajna: always iterative (no flash loan).
 */
export const submitLoop = async (
  wallet: WalletClient,
  req: LoopRequest,
): Promise<Hash> => {
  const leverageBps = leverageToBps(req.targetLeverage);
  const minLeverageBps = Math.round(
    leverageBps * (1 - req.slippageBps / 10_000),
  );
  const principalRaw = parseUnits(req.principal.toString(), CADC.decimals);

  const [path, ajnaLimitIndex] = await Promise.all([
    selectLoopPath(req.venue, principalRaw, leverageBps, req.ajnaStepFactorBps),
    req.venue === "ajna" ? readAjnaLimitIndex() : Promise.resolve(0n),
  ]);
  if (path === null) {
    throw new Error(
      "No execution path available: leverage exceeds the iterative ceiling.",
    );
  }

  if (path === "flash") {
    if (req.venue === "ajna") {
      return await wallet.writeContract({
        account: req.account,
        chain: null,
        address: VENUES.ajna.looper,
        abi: LOOPER_AJNA_ABI,
        functionName: "loop",
        args: [principalRaw, leverageBps, req.ajnaStepFactorBps, req.slippageBps, minLeverageBps, ajnaLimitIndex],
      });
    }
    return await wallet.writeContract({
      account: req.account,
      chain: null,
      address: VENUES[req.venue].looper,
      abi: LOOPER_MORPHO_ABI,
      functionName: "loop",
      args: [principalRaw, leverageBps, req.slippageBps, minLeverageBps],
    });
  }

  if (req.venue === "morpho") {
    return await wallet.writeContract({
      account: req.account,
      chain: null,
      address: VENUES.morpho.looper,
      abi: LOOPER_MORPHO_ABI,
      functionName: "loopIterative",
      args: [principalRaw, leverageBps, req.slippageBps, minLeverageBps],
    });
  }

  return await wallet.writeContract({
    account: req.account,
    chain: null,
    address: VENUES.ajna.looper,
    abi: LOOPER_AJNA_ABI,
    functionName: "loopIterative",
    args: [
      principalRaw,
      leverageBps,
      req.ajnaStepFactorBps,
      req.slippageBps,
      minLeverageBps,
      ajnaLimitIndex,
    ],
  });
};

/**
 * Grant the looper an (unbounded) USDC allowance so `unwind()` can pull the
 * caller's repayment USDC. One-time per wallet per looper deployment.
 */
export const approveUsdc = async (
  wallet: WalletClient,
  args: { readonly account: Address; readonly looper: Address },
): Promise<Hash> => {
  return await wallet.writeContract({
    account: args.account,
    chain: null,
    address: USDC.address,
    abi: erc20Abi,
    functionName: "approve",
    args: [args.looper, maxUint256],
  });
};

export type UnwindRequest = {
  readonly account: Address;
  readonly venue: VenueId;
  /** Max USDC the contract may pull and use to repay debt, raw 1e6 units. */
  readonly maxRepayRaw: bigint;
  /** CADC collateral the contract should withdraw, raw 1e18. */
  readonly collateralPullRaw: bigint;
};

export const submitUnwind = async (
  wallet: WalletClient,
  req: UnwindRequest,
): Promise<Hash> => {
  if (req.venue === "ajna") {
    const limitIndex = await readAjnaLimitIndex();
    return await wallet.writeContract({
      account: req.account,
      chain: null,
      address: VENUES.ajna.looper,
      abi: LOOPER_AJNA_ABI,
      functionName: "unwind",
      args: [req.maxRepayRaw, req.collateralPullRaw, limitIndex],
    });
  }
  return await wallet.writeContract({
    account: req.account,
    chain: null,
    address: VENUES[req.venue].looper,
    abi: LOOPER_MORPHO_ABI,
    functionName: "unwind",
    args: [req.maxRepayRaw, req.collateralPullRaw],
  });
};

export type UnwindBySwapRequest = {
  readonly account: Address;
  readonly venue: VenueId;
  /** CADC to withdraw and swap into USDC to repay the flash loan, raw 1e18. */
  readonly collateralToSwapRaw: bigint;
  /** CADC to withdraw and forward to the caller, raw 1e18. */
  readonly collateralToReturnRaw: bigint;
  /** Slippage tolerance for the CADC→USDC swap, in bps. */
  readonly slippageBps: number;
  /** Morpho-only: 0 = full debt; non-zero = partial repay (raw borrow shares). */
  readonly repaySharesPart?: bigint;
  /** Ajna-only: USDC native amount the contract will flash-borrow to repay debt, raw 1e6. */
  readonly maxRepayNativeRaw?: bigint;
  /** Per-call oracle band override in bps; 0 = use stored contract default. */
  readonly maxDivergenceBps?: number;
};

/**
 * Flash-loan unwind. Atomically flash-borrows USDC, repays debt,
 * withdraws collateral, swaps part to settle the flash loan.
 */
export const submitUnwindBySwap = async (
  wallet: WalletClient,
  req: UnwindBySwapRequest,
): Promise<Hash> => {
  if (req.venue === "ajna") {
    const limitIndex = await readAjnaLimitIndex();
    if (req.maxRepayNativeRaw == null || req.maxRepayNativeRaw === 0n) {
      // No debt: pull collateral only via unwind(0, collateral).
      return wallet.writeContract({
        account: req.account,
        chain: null,
        address: VENUES.ajna.looper,
        abi: LOOPER_AJNA_ABI,
        functionName: "unwind",
        args: [0n, req.collateralToReturnRaw, limitIndex],
      });
    }
    return wallet.writeContract({
      account: req.account,
      chain: null,
      address: VENUES.ajna.looper,
      abi: LOOPER_AJNA_ABI,
      functionName: "unwindBySwap",
      args: [
        req.maxRepayNativeRaw,
        req.collateralToSwapRaw,
        req.collateralToReturnRaw,
        req.slippageBps,
        limitIndex,
        req.maxDivergenceBps ?? 0,
      ],
    });
  }

  return await wallet.writeContract({
    account: req.account,
    chain: null,
    address: VENUES.morpho.looper,
    abi: LOOPER_MORPHO_ABI,
    functionName: "unwindBySwap",
    args: [
      req.collateralToSwapRaw,
      req.collateralToReturnRaw,
      req.slippageBps,
      req.repaySharesPart ?? 0n,
      req.maxDivergenceBps ?? 0,
    ],
  });
};
